import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/format';
import { Trash2, Search, KeyRound, Download, Percent, Eye, LogIn, ChevronDown, Pencil, ShieldCheck } from 'lucide-react';
import { UserSecurityDialog } from '@/components/admin/UserSecurityDialog';
import { exportToCsv } from '@/lib/csv';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { PlatformAccess } from '@/components/PlatformAccess';

export default function AdminUsersPage() {
    const { startImpersonation } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState('');
    const [pwdUser, setPwdUser] = useState(null);
    const [newPwd, setNewPwd] = useState('');
    const [pwdSaving, setPwdSaving] = useState(false);
    const [rateUser, setRateUser] = useState(null);
    const [rateForm, setRateForm] = useState({ olaparty: '', trumeet: '' });
    const [rateSaving, setRateSaving] = useState(false);
    const [portalUser, setPortalUser] = useState(null);
    const [portal, setPortal] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const [renameUser, setRenameUser] = useState(null);
    const [newName, setNewName] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [securityUser, setSecurityUser] = useState(null);
    const [flagged, setFlagged] = useState(new Set());
    useEffect(() => { api.get('/admin/multi-accounts').then(r => setFlagged(new Set(r.data.flagged_user_ids || []))).catch(() => {}); }, []);
    const [pStart, setPStart] = useState('');
    const [pEnd, setPEnd] = useState('');

    const portalInRange = (iso) => {
        const t = new Date(iso).getTime();
        if (pStart && t < new Date(pStart).getTime()) return false;
        if (pEnd && t > new Date(pEnd).getTime()) return false;
        return true;
    };

    const load = () => api.get('/admin/users').then(r => setUsers(r.data.users || []));
    useEffect(() => { load(); }, []);

    const openPortal = async (u) => {
        setPortalUser(u); setPortal(null); setPortalLoading(true); setPStart(''); setPEnd('');
        try {
            const { data } = await api.get(`/admin/users/${u.id}/portal`);
            setPortal(data);
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not load portal'); }
        finally { setPortalLoading(false); }
    };

    const exportUserRecharges = () => {
        if (!portal) return;
        const rows = [
            ...(portal.payments || []).map(p => ({
                time: p.created_at, source: p.kind === 'noble' ? 'Noble (paid)' : (p.kind === 'stock' ? 'Stock (paid)' : 'Paid recharge'),
                platform: p.platform, in_app_id: p.in_app_id, qty: p.diamonds_or_coins, amount_inr: p.amount_inr, status: p.status,
            })),
            ...(portal.stock_recharges || []).map(r => ({
                time: r.created_at, source: r.kind === 'noble' ? 'Noble (stock)' : 'Stock recharge',
                platform: r.platform, in_app_id: r.in_app_id, qty: r.amount, amount_inr: r.amount_inr || '', status: r.status,
            })),
        ].sort((a, b) => String(b.time).localeCompare(String(a.time))).filter(r => portalInRange(r.time));
        exportToCsv(`recharges-${portalUser?.email || 'user'}`, rows, [
            { header: 'Time', accessor: r => formatDateTime(r.time) },
            { header: 'Source', accessor: r => r.source },
            { header: 'Platform', accessor: r => r.platform },
            { header: 'In-app ID', accessor: r => r.in_app_id },
            { header: 'Quantity', accessor: r => r.qty },
            { header: 'Amount (INR)', accessor: r => r.amount_inr },
            { header: 'Status', accessor: r => r.status },
        ]);
    };

    const impersonate = async (u, readonly = false) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/impersonate`, null, { params: { readonly } });
            startImpersonation(data.user, data.readonly);
            // Full navigation (not react-router) so the admin route guard tears
            // down cleanly and the user session boots fresh on /dashboard.
            window.location.href = '/dashboard';
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start support session');
        }
    };

    const remove = async (id) => {        try { await api.delete(`/admin/users/${id}`); toast.success('User deleted'); load(); }
        catch { toast.error('Delete failed'); }
    };

    const toggleActive = async (u, next) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { active: next });
            setUsers(prev => prev.map(x => x.id === u.id ? { ...x, active: next } : x));
            toast.success(next ? `${u.email} enabled` : `${u.email} disabled (recharge blocked)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        }
    };

    const toggleLimit = async (u, next) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { limit_exempt: next });
            setUsers(prev => prev.map(x => x.id === u.id ? { ...x, limit_exempt: next } : x));
            toast.success(next ? `${u.email} whitelisted (no ₹15,000 limit)` : `${u.email} back to the ₹15,000 per-payment limit`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    };

    const toggleStockWallet = async (u, next) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { stock_wallet_unlocked: next });
            setUsers(prev => prev.map(x => x.id === u.id ? { ...x, stock_wallet_unlocked: next } : x));
            toast.success(next ? `Stock wallet unlocked for ${u.email}` : `Stock wallet locked for ${u.email}`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    };

    const savePlatforms = async (u, list) => {
        if (!list.length) { toast.error('Keep at least one platform enabled'); return; }
        try {
            await api.patch(`/admin/users/${u.id}`, { allowed_platforms: list });
            setUsers(prev => prev.map(x => x.id === u.id ? { ...x, allowed_platforms: list } : x));
            toast.success(`Platforms updated for ${u.email}`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    };

    const saveName = async () => {
        const name = newName.trim();
        if (!name) { toast.error('Enter a name'); return; }
        setRenaming(true);
        try {
            const { data } = await api.patch(`/admin/users/${renameUser.id}`, { name });
            setUsers(prev => prev.map(x => x.id === renameUser.id ? { ...x, name: data.name } : x));
            toast.success(`Renamed to ${data.name}`);
            setRenameUser(null);
        } catch (e) { toast.error(e.response?.data?.detail || 'Rename failed'); }
        finally { setRenaming(false); }
    };

    const resetPassword = async () => {
        if (newPwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setPwdSaving(true);
        try {
            await api.post(`/admin/users/${pwdUser.id}/reset-password`, { password: newPwd });
            toast.success(`Password reset for ${pwdUser.email}`);
            setPwdUser(null); setNewPwd('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed');
        } finally { setPwdSaving(false); }
    };

    const openRates = (u) => {
        const ov = u.rate_overrides || {};
        setRateForm({ olaparty: ov.olaparty ?? '', trumeet: ov.trumeet ?? '' });
        setRateUser(u);
    };

    const saveRates = async () => {
        setRateSaving(true);
        try {
            const { data } = await api.patch(`/admin/users/${rateUser.id}/rates`, {
                rates: {
                    olaparty: rateForm.olaparty === '' ? 0 : parseFloat(rateForm.olaparty),
                    trumeet: rateForm.trumeet === '' ? 0 : parseFloat(rateForm.trumeet),
                },
            });
            setUsers(prev => prev.map(x => x.id === rateUser.id ? { ...x, rate_overrides: data.rate_overrides } : x));
            toast.success(`Custom rates saved for ${rateUser.email}`);
            setRateUser(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save rates');
        } finally { setRateSaving(false); }
    };

    const filtered = users.filter(u => !q || (u.email + ' ' + (u.name || '') + ' ' + (u.whatsapp || '') + ' ' + (u.mobile || '')).toLowerCase().includes(q.toLowerCase()));

    return (
        <AdminShell title="Users">
            <Card className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input className="pl-9" placeholder="Search users..." value={q} onChange={e => setQ(e.target.value)} data-testid="admin-users-search-input" />
                    </div>
                    <div className="text-xs text-muted-foreground ml-auto">{filtered.length} of {users.length}</div>
                    <Button variant="outline" size="sm" onClick={() => exportToCsv('users', filtered, [
                        { header: 'Joined', accessor: (u) => u.created_at },
                        { header: 'Email', accessor: (u) => u.email },
                        { header: 'Name', accessor: (u) => u.name },
                        { header: 'WhatsApp', accessor: (u) => u.whatsapp || u.mobile },
                        { header: 'City', accessor: (u) => u.city },
                        { header: 'State', accessor: (u) => u.state },
                        { header: 'Pincode', accessor: (u) => u.pincode },
                        { header: 'Status', accessor: (u) => (u.active !== false ? 'Active' : 'Disabled') },
                    ])} data-testid="admin-users-export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow><TableHead>Joined</TableHead><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>WhatsApp</TableHead><TableHead>City</TableHead><TableHead>Status</TableHead><TableHead title="Whitelisted users can pay more than ₹15,000 per recharge">No limit</TableHead><TableHead title="Allow loading the stock wallet via bank transfer">Stock wallet</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {filtered.map(u => {
                                const active = u.active !== false;
                                return (
                                <TableRow key={u.id}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(u.created_at)}</TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1 group">
                                            <span data-testid={`admin-users-name-${u.id}`}>{u.name}</span>
                                            {flagged.has(u.id) && <Link to="/admin/multi-accounts" className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" title="Possible multiple accounts — review" data-testid={`admin-users-flagged-${u.id}`}>Multi-account?</Link>}
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-60 group-hover:opacity-100" onClick={() => { setRenameUser(u); setNewName(u.name || ''); }} title="Rename user" data-testid={`admin-users-rename-${u.id}`}><Pencil className="h-3 w-3" /></Button>
                                        </div>
                                        <div className="mt-1"><PlatformAccess size="sm" value={u.allowed_platforms ?? null} onChange={(v) => savePlatforms(u, v)} testid={`admin-users-platforms-${u.id}`} /></div>
                                    </TableCell>
                                    <TableCell>{u.whatsapp || u.mobile || '-'}</TableCell>
                                    <TableCell>{u.city ? `${u.city}, ${u.state}` : '-'}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Switch checked={active} onCheckedChange={(v) => toggleActive(u, v)} data-testid={`admin-users-active-toggle-${u.id}`} />
                                            <span className={`text-xs font-medium ${active ? 'text-emerald-600' : 'text-rose-600'}`} data-testid={`admin-users-status-${u.id}`}>{active ? 'Active' : 'Disabled'}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2" title="Whitelist: allow recharges above ₹15,000">
                                            <Switch checked={!!u.limit_exempt} onCheckedChange={(v) => toggleLimit(u, v)} data-testid={`admin-users-limit-exempt-toggle-${u.id}`} />
                                            <span className={`text-xs ${u.limit_exempt ? 'text-amber-700 font-medium' : 'text-muted-foreground'}`}>{u.limit_exempt ? 'Whitelisted' : '₹15k cap'}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Switch checked={!!u.stock_wallet_unlocked} onCheckedChange={(v) => toggleStockWallet(u, v)} data-testid={`admin-users-stock-wallet-toggle-${u.id}`} />
                                            <span className={`text-xs ${u.stock_wallet_unlocked ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}`} data-testid={`admin-users-stock-wallet-status-${u.id}`}>{u.stock_wallet_unlocked ? 'Unlocked' : 'Locked'}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => openPortal(u)} data-testid={`admin-users-view-portal-${u.id}`} title="View user's portal"><Eye className="h-4 w-4" /></Button>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="sm" data-testid={`admin-users-impersonate-${u.id}`} title="Login as this user (support mode)" className="text-[#1F9D8A] hover:text-[#0E2A47] gap-0.5"><LogIn className="h-4 w-4" /><ChevronDown className="h-3 w-3" /></Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => impersonate(u, false)} data-testid={`admin-users-impersonate-full-${u.id}`}><LogIn className="h-4 w-4 mr-2" /> Login as user (full)</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => impersonate(u, true)} data-testid={`admin-users-impersonate-readonly-${u.id}`}><Eye className="h-4 w-4 mr-2" /> Enter view-only</DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            <Button variant="ghost" size="sm" onClick={() => setSecurityUser(u)} data-testid={`admin-users-security-${u.id}`} title="Security: devices & IP history"><ShieldCheck className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => openRates(u)} data-testid={`admin-users-rates-${u.id}`} title="Custom recharge rates"><Percent className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => { setPwdUser(u); setNewPwd(''); }} data-testid={`admin-users-reset-password-${u.id}`} title="Reset password"><KeyRound className="h-4 w-4" /></Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button variant="ghost" size="sm" data-testid={`admin-users-delete-${u.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription>This will permanently delete {u.email}.</AlertDialogDescription></AlertDialogHeader>
                                                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(u.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No users found.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <UserSecurityDialog user={securityUser} onClose={() => setSecurityUser(null)} />

            <Dialog open={!!renameUser} onOpenChange={(o) => !o && setRenameUser(null)}>
                <DialogContent data-testid="admin-rename-user-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Rename user</DialogTitle>
                        <DialogDescription>Update the display/billing name for <strong>{renameUser?.email}</strong>. It's used on new invoices.</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-2 py-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Full name</Label>
                        <Input value={newName} maxLength={80} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveName()} data-testid="admin-rename-user-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameUser(null)}>Cancel</Button>
                        <Button className="bg-[#0E2A47] hover:bg-[#0B223A]" disabled={renaming || !newName.trim()} onClick={saveName} data-testid="admin-rename-user-save">{renaming ? 'Saving…' : 'Save name'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!pwdUser} onOpenChange={(o) => !o && setPwdUser(null)}>
                <DialogContent data-testid="admin-reset-password-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Reset user password</DialogTitle>
                        <DialogDescription>Set a new password for <strong>{pwdUser?.email}</strong>. Share it with the user manually.</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-2 py-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New password</Label>
                        <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Min 6 characters" data-testid="admin-user-new-password-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPwdUser(null)}>Cancel</Button>
                        <Button className="bg-[#0E2A47] hover:bg-[#0B223A]" disabled={pwdSaving || newPwd.length < 6} onClick={resetPassword} data-testid="admin-user-submit-reset-password">
                            {pwdSaving ? 'Saving…' : 'Set password'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!rateUser} onOpenChange={(o) => !o && setRateUser(null)}>
                <DialogContent data-testid="admin-user-rates-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Custom recharge rates</DialogTitle>
                        <DialogDescription>Set a custom <strong>₹ per diamond</strong> rate for <strong>{rateUser?.email}</strong>. Leave blank to use the platform default (₹1.30). Lower rate = customer gets more diamonds for the same ₹.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 py-2">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Olaparty (₹/💎)</Label>
                            <Input type="number" step="0.01" min="0" value={rateForm.olaparty} onChange={(e) => setRateForm(f => ({ ...f, olaparty: e.target.value }))} placeholder="default 1.30" data-testid="admin-user-rate-olaparty" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trumeet (₹/💎)</Label>
                            <Input type="number" step="0.01" min="0" value={rateForm.trumeet} onChange={(e) => setRateForm(f => ({ ...f, trumeet: e.target.value }))} placeholder="default 1.30" data-testid="admin-user-rate-trumeet" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRateUser(null)}>Cancel</Button>
                        <Button className="bg-[#0E2A47] hover:bg-[#0B223A]" disabled={rateSaving} onClick={saveRates} data-testid="admin-user-save-rates">
                            {rateSaving ? 'Saving…' : 'Save rates'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!portalUser} onOpenChange={(o) => { if (!o) { setPortalUser(null); setPortal(null); } }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-user-portal-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{portalUser?.name || portalUser?.email}'s portal</DialogTitle>
                        <DialogDescription>Read-only view of this user's balances, stock and complete recharge history.</DialogDescription>
                    </DialogHeader>
                    {portalLoading && <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>}
                    {portal && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {Object.entries(portal.balances || {}).map(([k, v]) => (
                                    <div key={k} className="rounded-lg border border-border p-3" data-testid={`portal-balance-${k}`}>
                                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground capitalize">{k} stock</div>
                                        <div className="font-display text-xl font-semibold tabular-nums">{(v.balance ?? 0).toLocaleString()}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="font-semibold text-sm">Recharge history</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Input type="datetime-local" value={pStart} max={pEnd || undefined} onChange={e => setPStart(e.target.value)} className="h-8 w-44 text-xs" data-testid="admin-user-portal-start" />
                                    <span className="text-xs text-muted-foreground">to</span>
                                    <Input type="datetime-local" value={pEnd} min={pStart || undefined} onChange={e => setPEnd(e.target.value)} className="h-8 w-44 text-xs" data-testid="admin-user-portal-end" />
                                    {(pStart || pEnd) && <Button variant="ghost" size="sm" onClick={() => { setPStart(''); setPEnd(''); }}>Clear</Button>}
                                    <Button variant="outline" size="sm" onClick={exportUserRecharges} data-testid="admin-user-portal-export-csv"><Download className="h-4 w-4 mr-1" /> Download recharge CSV</Button>
                                </div>
                            </div>
                            <div className="overflow-x-auto max-h-72 overflow-y-auto border border-border rounded-lg">
                                <Table>
                                    <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Source</TableHead><TableHead>Platform</TableHead><TableHead>In-app ID</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                        {[...(portal.payments || []).map(p => ({ ...p, _src: p.kind === 'noble' ? 'Noble (paid)' : (p.kind === 'stock' ? 'Stock (paid)' : 'Paid'), _qty: p.diamonds_or_coins })),
                                          ...(portal.stock_recharges || []).map(r => ({ ...r, _src: r.kind === 'noble' ? 'Noble (stock)' : 'Stock', _qty: r.amount }))]
                                            .filter(r => portalInRange(r.created_at))
                                            .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
                                            .map((r, i) => (
                                            <TableRow key={r.id || i}>
                                                <TableCell className="whitespace-nowrap text-xs">{formatDateTime(r.created_at)}</TableCell>
                                                <TableCell className="text-xs">{r._src}</TableCell>
                                                <TableCell className="capitalize text-xs">{r.platform}</TableCell>
                                                <TableCell className="font-mono text-xs">{r.in_app_id}</TableCell>
                                                <TableCell className="text-right tabular-nums">{(r._qty ?? 0).toLocaleString?.() ?? r._qty}</TableCell>
                                                <TableCell className="text-xs">{r.status}</TableCell>
                                            </TableRow>
                                        ))}
                                        {(portal.payments?.length || 0) + (portal.stock_recharges?.length || 0) === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6 text-sm">No recharges yet.</TableCell></TableRow>}
                                    </TableBody>
                                </Table>
                            </div>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="text-xs text-muted-foreground">Stock orders: {portal.stock_orders?.length || 0} · Allocations: {portal.allocations?.length || 0}</div>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={() => impersonate(portalUser, true)} data-testid="admin-user-portal-impersonate-readonly" className="gap-1.5"><Eye className="h-4 w-4" /> View-only visit</Button>
                                    <Button size="sm" className="bg-[#1F9D8A] hover:bg-[#18806f] gap-1.5" onClick={() => impersonate(portalUser, false)} data-testid="admin-user-portal-impersonate"><LogIn className="h-4 w-4" /> Login as this user</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </AdminShell>
    );
}
